import React, { MutableRefObject } from 'react';
import { token } from '@bynder/design-system';
import { IconError } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import useEditor from 'packages/hooks/useEditor';
import useMultiPageValidation from 'packages/hooks/useMultiPageValidation';
import generateTestId from 'packages/helpers/testIdHelpers';
import type { ExportConfigurationIds, Reviewer } from 'packages/hooks/useVirtualProperties';
import PublishModal from './PublishModal';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from 'packages/configs/features';
import RefreshModal from './RefreshModal';
import { DiscardChangesClickSource, ToggleShowUnpublishedChanges } from './types';
import { PublishButtonIndicator, PublishButtonStyled } from './Header.styled';

export type Props = {
    template: any;
    isInAction: boolean;
    saved: boolean;
    videosCount: number;
    showPublishModal: any;
    handlePublishModal: (isOpen: boolean) => void;
    publish: (
        creativeModel: any,
        exportIntegrations: any,
        exportConfigurationIds: ExportConfigurationIds,
        approvalEnabled?: boolean,
        reviewers?: Reviewer[],
    ) => void;
    closePublishModal: any;
};
type LocalProps = {
    discardChangesClickSource: MutableRefObject<DiscardChangesClickSource>;
    showUnpublishedChangesModal: boolean;
    toggleShowUnpublishedChanges: ToggleShowUnpublishedChanges;
    discardUnpublishedChanges: () => void;
};
const PublishButton = ({
    template,
    isInAction,
    saved,
    videosCount,
    showPublishModal,
    handlePublishModal,
    publish,
    closePublishModal,
    showUnpublishedChangesModal,
    toggleShowUnpublishedChanges,
    discardUnpublishedChanges,
    discardChangesClickSource,
}: Props & LocalProps) => {
    const { translate } = useTranslate();
    const {
        creativeModel,
        exportIntegrations,
        exportConfigurationIds,
        approvalEnabled,
        reviewers,
        type,
        isPublishing,
        setIsPublishing,
    } = useEditor();
    const { isPlatformAllowed } = useAccessRights();
    const { isValid, globalPropertiesValidations } = useMultiPageValidation();
    const isTemplate = type === 'TEMPLATE';
    const isDimensionInvalid = globalPropertiesValidations.some(
        (state) => !state.isValid && (state.validationMessages.width || state.validationMessages.height),
    );

    const enablePublish =
        !isInAction &&
        (isValid || (!isValid && isDimensionInvalid)) &&
        (!saved || template.status === 'DRAFT' || !template.lastPublishDate);
    const showModalOnPublish = !!videosCount;

    const handlePublish = () => {
        setIsPublishing(true);

        if (showUnpublishedChangesModal) {
            toggleShowUnpublishedChanges(false, 'publish');
        }

        if ((enablePublish && showModalOnPublish) || !isValid) {
            handlePublishModal(true);
        } else if (isPlatformAllowed([features.APPROVALS_ENABLED])) {
            publish(creativeModel, exportIntegrations, exportConfigurationIds, approvalEnabled, reviewers);
        } else {
            publish(creativeModel, exportIntegrations, exportConfigurationIds);
        }
    };

    const handleForcePublish = () => {
        setIsPublishing(true);
        publish(creativeModel, exportIntegrations, exportConfigurationIds, approvalEnabled, reviewers);
    };

    const publishId = generateTestId('editor_header_publish');

    return (
        <div>
            <PublishButtonStyled
                variant="primary"
                onClick={handlePublish}
                isDisabled={!enablePublish}
                isLoading={isInAction && isPublishing}
                rightIcon={
                    enablePublish &&
                    isDimensionInvalid && (
                        <PublishButtonIndicator>
                            <IconError size="20" style={{ color: token.red500 }} />
                        </PublishButtonIndicator>
                    )
                }
                {...publishId}
                {...{ id: 'editor_header_publish' }}
            >
                {isInAction && isPublishing
                    ? translate('editor.header.publishing')
                    : translate(isTemplate ? 'editor.header.publishChanges' : 'editor.header.publish')}
            </PublishButtonStyled>
            <PublishModal
                variationCount={videosCount}
                open={showPublishModal}
                loading={isInAction && isPublishing}
                onClose={() => {
                    closePublishModal();
                    setIsPublishing(false);
                }}
                isDimensionInvalid={isDimensionInvalid}
                onPublish={handleForcePublish}
            />
            <RefreshModal
                hasUnsavedChanges={!saved}
                isPublishDisabled={!enablePublish}
                isOpen={showUnpublishedChangesModal}
                loading={isInAction && isPublishing}
                toggle={(open) => {
                    toggleShowUnpublishedChanges(open, discardChangesClickSource?.current || 'preview');
                }}
                discard={discardUnpublishedChanges}
                onPublish={handlePublish}
            />
        </div>
    );
};

export default PublishButton;
